import { DATA_TEMPO_PREV_CODEBASE_ID } from "@/utils/jsx/extractElementsUtils";
import { ReactFramework } from "@/utils/templateProjects";
import { useActiveProject } from "@/utils/utils";
import { v4 as uuidv4 } from "uuid";
import {
  Columns2,
  FrameIcon,
  GlobeIcon,
  Grid2X2,
  LucideIcon,
  Rows2,
  SquareIcon,
  CircleIcon,
} from "lucide-react";

export enum LayoutId {
  APPLICATION = "application",
  BLANK = "blank",
  RECTANGLE = "rectangle",
  ELLIPSE = "ellipse",
  HORIZONTAL_LAYOUT = "horizontal-layout",
  VERTICAL_LAYOUT = "vertical-layout",
  GRID_LAYOUT = "grid-layout",
}

export type LayoutPreset = {
  id: LayoutId;
  name: string;
  getJsxWithCustomClasses?: (
    customClasses: string,
    customCodebaseId?: string,
  ) => string;
  getExpoJsx?: (customClasses: string, customCodebaseId?: string) => string;
  getHtmlWithCustomStyles: (
    extraStyles: string,
    customCodebaseId?: string,
  ) => string;
  getHtmlNoTailwind: (
    customStyles: string,
    customCodebaseId?: string,
  ) => string;
  icon: LucideIcon;
};

const options: LayoutPreset[] = [
  {
    id: LayoutId.BLANK,
    name: "Blank",
    getJsxWithCustomClasses: (
      customClasses: string,
      customCodebaseId?: string,
    ) =>
      `<div className="bg-white ${customClasses}" ${
        customCodebaseId
          ? `${DATA_TEMPO_PREV_CODEBASE_ID}="${customCodebaseId}"`
          : ""
      }></div>`,
    getHtmlWithCustomStyles: (
      customStyles: string,
      customCodebaseId?: string,
    ) => {
      const selectorId = uuidv4();
      return `<div class="temp-${selectorId}" data-tempoelementid="${customCodebaseId}"><style>:where(.temp-${selectorId}){background-color: white; ${customStyles}}</style></div>`;
    },
    getHtmlNoTailwind: (customStyles: string, customCodebaseId?: string) => {
      return `<div style="background-color: white; ${customStyles}" data-tempoelementid="${customCodebaseId}"></div>`;
    },
    icon: FrameIcon,
  },
  {
    id: LayoutId.RECTANGLE,
    name: "Rectangle",
    getJsxWithCustomClasses: (
      customClasses: string,
      customCodebaseId?: string,
    ) =>
      `<div className="bg-gray-200 ${customClasses}" ${
        customCodebaseId
          ? `${DATA_TEMPO_PREV_CODEBASE_ID}="${customCodebaseId}"`
          : ""
      }></div>`,
    getHtmlWithCustomStyles: (
      customStyles: string,
      customCodebaseId?: string,
    ) => {
      const selectorId = uuidv4();
      return `<div class="temp-${selectorId}" data-tempoelementid="${customCodebaseId}"><style>:where(.temp-${selectorId}){background-color: #e5e7eb; ${customStyles}}</style></div>`;
    },
    getHtmlNoTailwind: (customStyles: string, customCodebaseId?: string) => {
      return `<div style="background-color: #e5e7eb; ${customStyles}" data-tempoelementid="${customCodebaseId}"></div>`;
    },
    icon: SquareIcon,
  },
  {
    id: LayoutId.ELLIPSE,
    name: "Ellipse",
    getJsxWithCustomClasses: (
      customClasses: string,
      customCodebaseId?: string,
    ) =>
      `<div className="bg-gray-200 rounded-[50%] ${customClasses}" ${
        customCodebaseId
          ? `${DATA_TEMPO_PREV_CODEBASE_ID}="${customCodebaseId}"`
          : ""
      }></div>`,
    getHtmlWithCustomStyles: (
      customStyles: string,
      customCodebaseId?: string,
    ) => {
      const selectorId = uuidv4();
      return `<div class="temp-${selectorId}" data-tempoelementid="${customCodebaseId}"><style>:where(.temp-${selectorId}){background-color: #e5e7eb; border-radius: 50%; ${customStyles}}</style></div>`;
    },
    getHtmlNoTailwind: (customStyles: string, customCodebaseId?: string) => {
      return `<div style="background-color: #e5e7eb; border-radius: 50%; ${customStyles}" data-tempoelementid="${customCodebaseId}"></div>`;
    },
    icon: CircleIcon,
  },
  {
    id: LayoutId.HORIZONTAL_LAYOUT,
    name: "Horizontal Layout",
    getJsxWithCustomClasses: (
      customClasses: string,
      customCodebaseId?: string,
    ) =>
      `<div className="flex flex-row space-x-1 ${customClasses}" ${
        customCodebaseId
          ? `${DATA_TEMPO_PREV_CODEBASE_ID}="${customCodebaseId}"`
          : ""
      }><div className="bg-blue-100 flex-1">01</div><div className="bg-blue-200 flex-1">02</div></div>`,
    getHtmlWithCustomStyles: (
      customStyles: string,
      customCodebaseId?: string,
    ) => {
      const selectorId = uuidv4();
      return `<div class="temp-${selectorId}" data-tempoelementid="${customCodebaseId}"><style>:where(.temp-${selectorId}){display: flex;margin-left: 0.25rem;flex-direction: row;${customStyles}}</style><div style="background-color: #DBEAFE;flex: 1;">01</div><div style="background-color: #BFDBFE;flex: 1;">02</div></div>`;
    },
    getHtmlNoTailwind: (customStyles: string, customCodebaseId?: string) => {
      return `<div style="display: flex;margin-left: 0.25rem;flex-direction: row;${customStyles}" data-tempoelementid="${customCodebaseId}"><div style="background-color: #DBEAFE;flex: 1;">01</div><div style="background-color: #BFDBFE;flex: 1;">02</div></div>`;
    },
    icon: Columns2,
  },
  {
    id: LayoutId.VERTICAL_LAYOUT,
    name: "Vertical Layout",
    getJsxWithCustomClasses: (
      customClasses: string,
      customCodebaseId?: string,
    ) =>
      `<div className="flex flex-col space-y-1 ${customClasses}" ${
        customCodebaseId
          ? `${DATA_TEMPO_PREV_CODEBASE_ID}="${customCodebaseId}"`
          : ""
      }><div className="bg-blue-100 flex-1">01</div><div className="bg-blue-200 flex-1">02</div></div>`,
    getHtmlWithCustomStyles: (
      customStyles: string,
      customCodebaseId?: string,
    ) => {
      const selectorId = uuidv4();
      return `<div class="temp-${selectorId}" data-tempoelementid="${customCodebaseId}"><style>:where(.temp-${selectorId}){display: flex;margin-top: 0.25rem;flex-direction: column;${customStyles}}</style><div style="background-color: #DBEAFE;flex: 1;">01</div><div style="background-color: #BFDBFE;flex: 1;">02</div></div>`;
    },
    getHtmlNoTailwind: (customStyles: string, customCodebaseId?: string) => {
      return `<div style="display: flex;margin-top: 0.25rem;flex-direction: column;${customStyles}" data-tempoelementid="${customCodebaseId}"><div style="background-color: #DBEAFE;flex: 1;">01</div><div style="background-color: #BFDBFE;flex: 1;">02</div></div>`;
    },
    icon: Rows2,
  },
  {
    id: LayoutId.GRID_LAYOUT,
    name: "Grid Layout",
    getJsxWithCustomClasses: (
      customClasses: string,
      customCodebaseId?: string,
    ) =>
      `<div className="grid grid-cols-2 grid-rows-2 gap-1 ${customClasses}" ${
        customCodebaseId
          ? `${DATA_TEMPO_PREV_CODEBASE_ID}="${customCodebaseId}"`
          : ""
      }><div className="bg-blue-100">01</div><div className="bg-blue-200">02</div><div className="bg-blue-300">03</div><div className="bg-blue-400">04</div></div>`,
    getHtmlWithCustomStyles: (
      customStyles: string,
      customCodebaseId?: string,
    ) => {
      const selectorId = uuidv4();
      return `<div class="temp-${selectorId}" data-tempoelementid="${customCodebaseId}"><style>:where(.temp-${selectorId}){display: grid; grid-template-columns: repeat(2, minmax(0, 1fr));grid-template-rows: repeat(2, minmax(0, 1fr));gap: 0.25rem;${customStyles}}</style><div style="background-color: #DBEAFE;">01</div><div style="background-color: #BFDBFE;">02</div><div style="background-color: #93C5FD;">03</div><div style="background-color: #60A5FA;">04</div></div>`;
    },
    getHtmlNoTailwind: (customStyles: string, customCodebaseId?: string) => {
      return `<div style="display: grid; grid-template-columns: repeat(2, minmax(0, 1fr));grid-template-rows: repeat(2, minmax(0, 1fr));gap: 0.25rem;${customStyles}" data-tempoelementid="${customCodebaseId}"><div style="background-color: #DBEAFE;">01</div><div style="background-color: #BFDBFE;">02</div><div style="background-color: #93C5FD;">03</div><div style="background-color: #60A5FA;">04</div></div>`;
    },
    icon: Grid2X2,
  },
  {
    id: LayoutId.APPLICATION,
    name: "Route",
    getJsxWithCustomClasses: (
      customClasses: string,
      customCodebaseId?: string,
    ) => ``,
    getHtmlWithCustomStyles: (customStyles: string) => ``,
    getHtmlNoTailwind: (customStyles: string) => ``,
    icon: GlobeIcon,
  },
];

const getExpoStyleString = (customStyles: { [key: string]: string }) => {
  return `{${Object.entries(customStyles)
    .map(([key, value]) => `${key}: "${value}"`)
    .join(",")}}`;
};

const expoOptions: LayoutPreset[] = [
  {
    id: LayoutId.BLANK,
    name: "Blank",
    getExpoJsx: (customClasses: string, customCodebaseId?: string) => {
      return `<View className="bg-white ${customClasses}" ${
        customCodebaseId
          ? `${DATA_TEMPO_PREV_CODEBASE_ID}="${customCodebaseId}"`
          : ""
      }></View>`;
    },
    getHtmlWithCustomStyles: (
      customStyles: string,
      customCodebaseId?: string,
    ) => {
      const selectorId = uuidv4();
      return `<div class="temp-${selectorId}" data-tempoelementid="${customCodebaseId}"><style>:where(.temp-${selectorId}){background-color: white; ${customStyles}}</style></div>`;
    },
    getHtmlNoTailwind: (customStyles: string, customCodebaseId?: string) => {
      return `<div style="background-color: white; ${customStyles}" data-tempoelementid="${customCodebaseId}"></div>`;
    },
    icon: FrameIcon,
  },
  {
    id: LayoutId.RECTANGLE,
    name: "Rectangle",
    getExpoJsx: (customClasses: string, customCodebaseId?: string) => {
      return `<View className="bg-gray-200 ${customClasses}" ${
        customCodebaseId
          ? `${DATA_TEMPO_PREV_CODEBASE_ID}="${customCodebaseId}"`
          : ""
      }></View>`;
    },
    getHtmlWithCustomStyles: (
      customStyles: string,
      customCodebaseId?: string,
    ) => {
      const selectorId = uuidv4();
      return `<div class="temp-${selectorId}" data-tempoelementid="${customCodebaseId}"><style>:where(.temp-${selectorId}){background-color: #e5e7eb; ${customStyles}}</style></div>`;
    },
    getHtmlNoTailwind: (customStyles: string, customCodebaseId?: string) => {
      return `<div style="background-color: #e5e7eb; ${customStyles}" data-tempoelementid="${customCodebaseId}"></div>`;
    },
    icon: SquareIcon,
  },
  {
    id: LayoutId.ELLIPSE,
    name: "Ellipse",
    getExpoJsx: (customClasses: string, customCodebaseId?: string) => {
      return `<View className="bg-gray-200 rounded-[50%] ${customClasses}" ${
        customCodebaseId
          ? `${DATA_TEMPO_PREV_CODEBASE_ID}="${customCodebaseId}"`
          : ""
      }></View>`;
    },
    getHtmlWithCustomStyles: (
      customStyles: string,
      customCodebaseId?: string,
    ) => {
      const selectorId = uuidv4();
      return `<div class="temp-${selectorId}" data-tempoelementid="${customCodebaseId}"><style>:where(.temp-${selectorId}){background-color: #e5e7eb; border-radius: 50%; ${customStyles}}</style></div>`;
    },
    getHtmlNoTailwind: (customStyles: string, customCodebaseId?: string) => {
      return `<div style="background-color: #e5e7eb; border-radius: 50%; ${customStyles}" data-tempoelementid="${customCodebaseId}"></div>`;
    },
    icon: CircleIcon,
  },
  {
    id: LayoutId.HORIZONTAL_LAYOUT,
    name: "Horizontal Layout",
    getExpoJsx: (customClasses: string, customCodebaseId?: string) => {
      return `<View className="flex flex-row space-x-1 ${customClasses}" ${
        customCodebaseId
          ? `${DATA_TEMPO_PREV_CODEBASE_ID}="${customCodebaseId}"`
          : ""
      }><View className="bg-blue-100 flex-1">01</View><View className="bg-blue-200 flex-1">02</View></View>`;
    },
    getHtmlWithCustomStyles: (
      customStyles: string,
      customCodebaseId?: string,
    ) => {
      const selectorId = uuidv4();
      return `<div class="temp-${selectorId}" data-tempoelementid="${customCodebaseId}"><style>:where(.temp-${selectorId}){display: flex;margin-left: 0.25rem;flex-direction: row;${customStyles}}</style><div style="background-color: #DBEAFE;flex: 1;">01</div><div style="background-color: #BFDBFE;flex: 1;">02</div></div>`;
    },
    getHtmlNoTailwind: (customStyles: string, customCodebaseId?: string) => {
      return `<div style="display: flex;margin-left: 0.25rem;flex-direction: row;${customStyles}" data-tempoelementid="${customCodebaseId}"><div style="background-color: #DBEAFE;flex: 1;">01</div><div style="background-color: #BFDBFE;flex: 1;">02</div></div>`;
    },
    icon: Columns2,
  },
  {
    id: LayoutId.VERTICAL_LAYOUT,
    name: "Vertical Layout",
    getExpoJsx: (customClasses: string, customCodebaseId?: string) => {
      return `<View className="flex flex-col space-y-1 ${customClasses}" ${
        customCodebaseId
          ? `${DATA_TEMPO_PREV_CODEBASE_ID}="${customCodebaseId}"`
          : ""
      }><View className="bg-blue-100 flex-1">01</View><View className="bg-blue-200 flex-1">02</View></View>`;
    },
    getHtmlWithCustomStyles: (
      customStyles: string,
      customCodebaseId?: string,
    ) => {
      const selectorId = uuidv4();
      return `<div class="temp-${selectorId}" data-tempoelementid="${customCodebaseId}"><style>:where(.temp-${selectorId}){display: flex;margin-top: 0.25rem;flex-direction: column;${customStyles}}</style><div style="background-color: #DBEAFE;flex: 1;">01</div><div style="background-color: #BFDBFE;flex: 1;">02</div></div>`;
    },
    getHtmlNoTailwind: (customStyles: string, customCodebaseId?: string) => {
      return `<div style="display: flex;margin-top: 0.25rem;flex-direction: column;${customStyles}" data-tempoelementid="${customCodebaseId}"><div style="background-color: #DBEAFE;flex: 1;">01</div><div style="background-color: #BFDBFE;flex: 1;">02</div></div>`;
    },
    icon: Rows2,
  },
  {
    id: LayoutId.GRID_LAYOUT,
    name: "Grid Layout",
    getExpoJsx: (customClasses: string, customCodebaseId?: string) => {
      return `<View className="grid grid-cols-2 grid-rows-2 gap-1 ${customClasses}" ${
        customCodebaseId
          ? `${DATA_TEMPO_PREV_CODEBASE_ID}="${customCodebaseId}"`
          : ""
      }><View className="bg-blue-100">01</View><View className="bg-blue-200">02</View><View className="bg-blue-300">03</View><View className="bg-blue-400">04</View></View>`;
    },
    getHtmlWithCustomStyles: (
      customStyles: string,
      customCodebaseId?: string,
    ) => {
      const selectorId = uuidv4();
      return `<div class="temp-${selectorId}" data-tempoelementid="${customCodebaseId}"><style>:where(.temp-${selectorId}){display: grid; grid-template-columns: repeat(2, minmax(0, 1fr));grid-template-rows: repeat(2, minmax(0, 1fr));gap: 0.25rem;${customStyles}}</style><div style="background-color: #DBEAFE;">01</div><div style="background-color: #BFDBFE;">02</div><div style="background-color: #93C5FD;">03</div><div style="background-color: #60A5FA;">04</div></div>`;
    },
    getHtmlNoTailwind: (customStyles: string, customCodebaseId?: string) => {
      return `<div style="display: grid; grid-template-columns: repeat(2, minmax(0, 1fr));grid-template-rows: repeat(2, minmax(0, 1fr));gap: 0.25rem;${customStyles}" data-tempoelementid="${customCodebaseId}"><div style="background-color: #DBEAFE;">01</div><div style="background-color: #BFDBFE;">02</div><div style="background-color: #93C5FD;">03</div><div style="background-color: #60A5FA;">04</div></div>`;
    },
    icon: Grid2X2,
  },
  {
    id: LayoutId.APPLICATION,
    name: "Route",
    getExpoJsx: (customClasses: string, customCodebaseId?: string) => ``,
    getHtmlWithCustomStyles: (customStyles: string) => ``,
    getHtmlNoTailwind: (customStyles: string) => ``,
    icon: GlobeIcon,
  },
];

const useLayoutPresets = () => {
  const project = useActiveProject();

  return {
    options: project?.framework === ReactFramework.EXPO ? expoOptions : options,
  };
};

export default useLayoutPresets;
